import type { CompanySlug, RouteName, StaffRole } from "../types.js";
import { clear, h } from "../dom.js";
import {
  adminDestinationsForRole,
  moreGroupsForRole,
  primaryNavForRole,
  type NavDestination,
  type ShellRoute,
} from "../nav-registry.js";

export interface SystemPageOptions {
  root: HTMLElement;
  navigate: (route: RouteName) => Promise<void>;
  role: StaffRole;
  /** Optional company slug to highlight on Health detail. */
  company?: CompanySlug;
}

const SETTINGS_ROUTES: readonly ShellRoute[] = ["rules", "health"];

/**
 * Admin — the plain index of the tools that are not one of the owner's
 * primary tabs or four More groups. The account manager sees only pages they
 * can open; owner-only rows never appear here.
 */
export function renderSystemPage(opts: SystemPageOptions): void {
  const { root, navigate, role } = opts;
  clear(root);
  root.append(h("h1", { class: "page-title" }, ["Admin"]));
  root.append(
    h("p", { class: "lede" }, [
      role === "account_manager"
        ? "The rest of the pages you can open."
        : "The rest of the tools, in the same plain groups as More.",
    ]),
  );

  const destinations = destinationIndex(role);
  const list = h("ul", { class: "system-list", "data-role": "system-landing" });
  const seen = new Set<ShellRoute>();

  const section = (heading: string, ids: readonly ShellRoute[], headingRole?: string): void => {
    const rows = ids
      .map((id) => destinations.get(id))
      .filter((dest): dest is NavDestination => dest !== undefined)
      .filter((dest) => !seen.has(dest.id));
    if (rows.length === 0) return;
    const headingNode = h("li", { class: "system-group-title" }, [heading]);
    if (headingRole !== undefined) headingNode.setAttribute("data-role", headingRole);
    list.append(headingNode);
    for (const dest of rows) {
      seen.add(dest.id);
      list.append(link(dest, navigate));
    }
  };

  // No "Workbench" group: it is not a heading the menu has (`nav-registry.ts`),
  // and inventing one made the Admin page read like a second, differently
  // grouped menu. Its rows are reachable from More, and the ones that are
  // Admin destinations are listed under "Admin" below.
  section("Settings", SETTINGS_ROUTES);
  section("Admin", adminDestinationsForRole(role).map((dest) => dest.id));

  root.append(list);
}

function destinationIndex(role: StaffRole): Map<ShellRoute, NavDestination> {
  const out = new Map<ShellRoute, NavDestination>();
  const add = (dest: NavDestination): void => {
    if (!out.has(dest.id)) out.set(dest.id, dest);
  };
  for (const dest of primaryNavForRole(role)) add(dest);
  for (const group of moreGroupsForRole(role)) {
    for (const dest of group.destinations) add(dest);
  }
  for (const dest of adminDestinationsForRole(role)) add(dest);
  return out;
}

function link(
  dest: NavDestination,
  navigate: (route: RouteName) => Promise<void>,
): HTMLLIElement {
  const btn = h(
    "button",
    {
      type: "button",
      class: "system-link",
      "data-system-route": dest.id,
      "aria-label": `Go to ${dest.label}`,
    },
    [dest.label],
  );
  btn.addEventListener("click", () => {
    void navigate(dest.id as RouteName);
  });
  return h("li", {}, [btn]);
}
